import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowLeft, CalendarClock, MessageCircle, ChevronDown,
  FileText, Reply, Headset, Receipt, Package, BarChart3,
  TrendingUp, Megaphone, Workflow, Calculator, Users, LineChart,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/sections/Footer";
import DemoModal from "@/components/DemoModal";
import WhatsAppAssistant from "@/components/WhatsAppAssistant";
import Seo from "@/components/Seo";
import { getConfig } from "@/lib/api";
import { useLanguage } from "@/i18n/LanguageContext";

const AUTO_ICONS = [FileText, Reply, Headset, Receipt, Package, BarChart3];
const AGENT_ICONS = { sales: TrendingUp, marketing: Megaphone, operations: Workflow, finance: Calculator, hr: Users, executive: LineChart };

const AgentsServicePage = () => {
  const { t, lang } = useLanguage();
  const [whatsapp, setWhatsapp] = useState("");
  const [demoOpen, setDemoOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const openDemo = useCallback(() => setDemoOpen(true), []);

  const d = t.agentsService;
  const sp = t.servicePages;

  useEffect(() => {
    getConfig()
      .then((c) => setWhatsapp(c.whatsapp_number || ""))
      .catch(() => {});
  }, []);

  const waLink = useCallback(
    (text) =>
      `https://wa.me/${(whatsapp || "41782042172").replace(/\D/g, "")}?text=${encodeURIComponent(text)}`,
    [whatsapp]
  );

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: d.faq.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="bg-void text-white font-body min-h-screen" data-testid="agents-service-page">
      <Seo title={d.metaTitle} description={d.metaDesc} path="/servizi/agenti-ai-per-aziende" lang={lang} jsonLd={faqJsonLd} />
      <div className="grain-overlay" />
      <Navbar onOpenDemo={openDemo} waLink={waLink} />
      <main className="pt-16">
        <section className="pt-12 pb-16 lg:pt-16 lg:pb-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <a href="/" data-testid="service-back-link" className="inline-flex items-center gap-2 text-sm text-neon-cyan hover:gap-3 transition-all mb-10">
                <ArrowLeft size={16} /> {sp.back}
              </a>
              <span className="inline-block text-xs font-bold tracking-widest uppercase border border-neon-cyan/40 text-neon-cyan rounded-full px-3 py-1 mb-6">
                {d.chip}
              </span>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight max-w-4xl" data-testid="service-title">
                {d.h1}
              </h1>
              <p className="mt-6 text-base lg:text-lg text-white/70 max-w-3xl" data-testid="service-subtitle">
                {d.subtitle}
              </p>
            </motion.div>
          </div>
        </section>

        <section className="py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
              data-testid="agents-problema-panel"
              className="glass-panel spotlight rounded-3xl p-10 sm:p-12 relative overflow-hidden"
            >
              <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-neon-magenta/20 blur-[90px] pointer-events-none" />
              <h2 className="relative font-display text-2xl sm:text-3xl font-bold tracking-tight mb-5">{d.problemaTitle}</h2>
              <p className="relative text-base text-white/70 leading-relaxed">{d.problemaText}</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: 0.12 }}
              data-testid="agents-soluzione-panel"
              className="glass-panel spotlight rounded-3xl p-10 sm:p-12 relative overflow-hidden"
            >
              <div className="absolute -bottom-20 -left-20 w-60 h-60 rounded-full bg-neon-cyan/20 blur-[90px] pointer-events-none" />
              <h2 className="relative font-display text-2xl sm:text-3xl font-bold tracking-tight mb-5">{d.soluzioneTitle}</h2>
              <p className="relative text-base text-white/70 leading-relaxed">{d.soluzioneText}</p>
            </motion.div>
          </div>
        </section>

        <section className="py-16 lg:py-20" data-testid="agents-automations-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5 }}
              className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-12"
            >
              {d.automationsTitle}
            </motion.h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {d.automations.map((a, i) => {
                const Icon = AUTO_ICONS[i % AUTO_ICONS.length];
                return (
                  <motion.div
                    key={a.title}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.5, delay: (i % 3) * 0.12 }}
                    data-testid={`automation-card-${i}`}
                    className="glass-panel spotlight rounded-2xl p-7 hover:-translate-y-1.5 hover:shadow-[0_0_40px_rgba(0,240,255,0.12)] transition-all duration-500"
                  >
                    <Icon size={24} className="text-neon-cyan mb-4" />
                    <h3 className="font-display text-lg font-bold tracking-tight mb-2">{a.title}</h3>
                    <p className="text-sm text-white/70 leading-relaxed">{a.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-16 lg:py-20" data-testid="agents-team-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5 }}
              className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-12"
            >
              {d.agentsTitle}
            </motion.h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {t.agents.list.map((a, i) => {
                const Icon = AGENT_ICONS[a.id];
                return (
                  <motion.div
                    key={a.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.5, delay: (i % 3) * 0.12 }}
                    data-testid={`agents-page-card-${a.id}`}
                    className="glass-panel spotlight rounded-2xl p-8 hover:-translate-y-1.5 hover:shadow-[0_0_30px_rgba(112,0,255,0.25)] transition-all duration-500"
                  >
                    <Icon size={24} className="text-neon-violet mb-4" />
                    <h3 className="font-display text-xl font-bold mb-3">{a.name}</h3>
                    <p className="text-sm text-white/70 leading-relaxed mb-5">{a.desc}</p>
                    <div className="flex flex-wrap gap-2">
                      {a.tags.map((tag) => (
                        <span key={tag} className="text-xs border border-white/10 rounded-full px-3 py-1 text-white/60">{tag}</span>
                      ))}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
              data-testid="agents-integrations-panel"
              className="glass-panel spotlight rounded-3xl p-10 sm:p-14 relative overflow-hidden"
            >
              <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-neon-violet/20 blur-[100px] pointer-events-none" />
              <div className="relative max-w-3xl">
                <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-6">{d.integrationsTitle}</h2>
                <p className="text-base lg:text-lg text-white/75 leading-relaxed">{d.integrationsText}</p>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="py-16 lg:py-20" data-testid="agents-method-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5 }}
              className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-12"
            >
              {t.method.titleA} <span className="neon-text">{t.method.titleNeon}</span>
            </motion.h2>
            <div className="flex flex-col gap-4">
              {t.method.steps.map((s, i) => (
                <motion.div
                  key={s.n}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  data-testid={`agents-method-step-${i}`}
                  className="glass-panel spotlight rounded-xl px-6 py-5 flex items-start gap-6 hover:border-neon-cyan/30 transition-colors duration-300"
                >
                  <span className="font-mono text-neon-cyan text-lg font-bold shrink-0 mt-0.5">{s.n}</span>
                  <div>
                    <h3 className="font-display font-bold text-base sm:text-lg">{s.title}</h3>
                    <p className="text-sm text-white/70 mt-1 leading-relaxed">{s.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 lg:py-20" data-testid="agents-faq-section">
          <div className="max-w-3xl mx-auto px-6 lg:px-12">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5 }}
              className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-12"
            >
              {d.faqTitle}
            </motion.h2>
            <div className="flex flex-col gap-4">
              {d.faq.map((f, i) => (
                <motion.div
                  key={f.q}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  data-testid={`faq-item-${i}`}
                  className="glass-panel spotlight rounded-xl overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                    aria-expanded={openFaq === i}
                    data-testid={`faq-question-${i}`}
                    className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                  >
                    <span className="font-semibold text-sm sm:text-base">{f.q}</span>
                    <ChevronDown size={18} className={`shrink-0 text-neon-cyan transition-transform duration-300 ${openFaq === i ? "rotate-180" : ""}`} />
                  </button>
                  {openFaq === i && (
                    <div className="px-6 pb-5 text-sm text-white/70 leading-relaxed" data-testid={`faq-answer-${i}`}>{f.a}</div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl"
            >
              <p className="text-xs font-bold tracking-[0.3em] uppercase text-neon-cyan mb-4">{d.ctaOverline}</p>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">{d.ctaTitle}</h2>
              <p className="mt-5 text-base lg:text-lg text-white/70">{d.ctaText}</p>
              <div className="mt-10 flex flex-wrap gap-4">
                <button
                  onClick={openDemo}
                  data-testid="agents-cta-demo"
                  className="flex items-center gap-2 btn-cyber bg-gradient-to-r from-neon-cyan to-neon-violet px-8 py-4 font-semibold text-void hover:opacity-90 hover:-translate-y-0.5 transition-all duration-300"
                >
                  <CalendarClock size={18} /> {sp.ctaPrimary}
                </button>
                <a
                  href={waLink(d.waMessage)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="agents-cta-whatsapp"
                  className="flex items-center gap-2 btn-cyber border border-white/20 px-8 py-4 font-semibold hover:border-neon-cyan/60 hover:text-neon-cyan hover:-translate-y-0.5 transition-all duration-300"
                >
                  <MessageCircle size={18} /> {t.cta.whatsapp}
                </a>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer waLink={waLink} />
      <WhatsAppAssistant whatsapp={whatsapp} />
      <DemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
    </div>
  );
};

export default AgentsServicePage;
